import React, { useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import ActionDialog, { ACTIONS } from "./actionDialog";
import { formatDateTime, getStatusDisplay } from "./helpers";

const STAGES = [10, 50, 100];

const getStageColor = (stage, release) => {
  if (release.status === "stable") return "success";
  // Approval and publish sit between 50% and 100%.
  const current =
    release.status === "approval" || release.status === "publish"
      ? 100
      : release.stage;
  if (!current || stage > current) return "hollow";
  if (stage < current) return "success";
  return release.status === "paused" || release.status === "blocked"
    ? "warning"
    : "primary";
};

const getBanner = (release) => {
  const { status, version } = release;
  if (release.mode === "direct") {
    return {
      color: "primary",
      title: `${version} was uploaded with --direct, so every outlet got it at once (bridge release).`,
    };
  }
  switch (status) {
    case "draft":
      return { color: "primary", title: `${version} is uploaded. No outlet has it yet.` };
    case "paused":
      return { color: "warning", title: "Paused. No new outlet is getting this version." };
    case "approval":
      return { color: "warning", title: "Waiting for a second admin to approve 100%." };
    case "publish":
      return {
        color: "primary",
        title: `Approved. Run npm run release:publish ${version} to send it to every outlet. This page marks it Stable within a minute of the publish.`,
      };
    case "rolled_back":
      return { color: "danger", title: `${version} was rolled back. Every outlet is on the previous version again.` };
    case "blocked":
      return { color: "danger", title: `${version} is blocked. Picked outlets are going back to the old version.` };
    case "stable":
      return { color: "success", title: `${version} is live on every outlet.` };
    default:
      return null;
  }
};

const ReleaseSummary = ({ release }) => {
  const [activeAction, setActiveAction] = useState(null);
  const status = getStatusDisplay(release.status);
  const banner = getBanner(release);
  const counts = release.counts || {};
  const actions = (release.allowed_actions || []).filter(
    (action) =>
      ACTIONS[action] &&
      action !== "set_outlets" &&
      action !== "safe_to_roll_back",
  );
  // Rollback safety only matters while a rollback is still possible.
  const canMarkSafety =
    release.mode !== "direct" &&
    !["blocked", "rolled_back"].includes(release.status);
  const showDayCounter =
    release.mode === "random" &&
    release.stage &&
    release.stage < 100 &&
    (release.status === "rolling" || release.status === "paused");

  return (
    <>
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiTitle size="s">
            <h2>{release.version}</h2>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiBadge color={status.color}>{status.label}</EuiBadge>
        </EuiFlexItem>
      </EuiFlexGroup>

      {banner ? (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut size="s" color={banner.color} title={banner.title} />
        </>
      ) : null}

      {release.short_by > 0 ? (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            size="s"
            color="warning"
            iconType="alert"
            title={`Short by ${release.short_by} outlets. Fewer outlets are eligible than this stage needs, so all eligible outlets were picked.`}
          />
        </>
      ) : null}

      {release.safe_to_roll_back === false ? (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            size="s"
            color="warning"
            iconType="alert"
            title="Not safe to roll back. A problem must be fixed forward with a new version."
          />
        </>
      ) : null}

      {release.mode === "random" ? (
        <>
          <EuiSpacer size="m" />
          <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
            {STAGES.map((stage, index) => (
              <React.Fragment key={stage}>
                {index > 0 ? (
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="arrowRight" />
                  </EuiFlexItem>
                ) : null}
                <EuiFlexItem grow={false}>
                  <EuiBadge color={getStageColor(stage, release)}>
                    {stage}%
                  </EuiBadge>
                </EuiFlexItem>
              </React.Fragment>
            ))}
            {showDayCounter ? (
              <EuiFlexItem grow={false}>
                <EuiText size="s" color="subdued">
                  Day {release.days_in_stage}
                  {release.suggested_wait_days
                    ? `, suggested ${release.suggested_wait_days.min}–${release.suggested_wait_days.max}`
                    : ""}
                  {release.stage_started_at
                    ? ` · started ${formatDateTime(release.stage_started_at)}`
                    : ""}
                </EuiText>
              </EuiFlexItem>
            ) : null}
          </EuiFlexGroup>
        </>
      ) : null}

      <EuiSpacer size="m" />
      <EuiFlexGroup responsive={false} wrap>
        {[
          ["Total outlets", counts.total],
          ["Picked", counts.picked],
          [`On ${release.version}`, counts.on_new],
          ["On old version", counts.on_old],
          ["On hold", counts.on_hold],
        ].map(([label, value]) => (
          <EuiFlexItem grow={false} key={label}>
            <EuiStat
              title={value === undefined ? "-" : value}
              description={label}
              titleSize="s"
            />
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>

      {actions.length > 0 || canMarkSafety ? (
        <>
          <EuiSpacer size="m" />
          <EuiFlexGroup gutterSize="s" responsive={false} wrap>
            {canMarkSafety ? (
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  size="s"
                  color="warning"
                  onClick={() => setActiveAction("safe_to_roll_back")}
                >
                  {ACTIONS.safe_to_roll_back.label(release)}
                </EuiButtonEmpty>
              </EuiFlexItem>
            ) : null}
            {actions.map((action) => (
              <EuiFlexItem grow={false} key={action}>
                <EuiButton
                  size="s"
                  color={ACTIONS[action].color}
                  fill={ACTIONS[action].fill}
                  onClick={() => setActiveAction(action)}
                >
                  {ACTIONS[action].label(release)}
                </EuiButton>
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>
        </>
      ) : null}

      {activeAction ? (
        <ActionDialog
          action={activeAction}
          release={release}
          onClose={() => setActiveAction(null)}
        />
      ) : null}
    </>
  );
};

export default ReleaseSummary;
